import i18n from '../../../i18n'
import { renderCurrency } from '../../../utils/property'
import { FORM_DIRECTION, REAL_ESTATE_TYPES } from '../../constants'
import responseFields from '../../constants/responseFields'
import { contactProfileDetailSearchFieldTypes, contactProfileFieldTypes } from '../contacts/fieldTypes'
import { companyGeneralFields, companyOtherInfoFields, companyProfileFields } from './fields'
import {
    companyDetailSearchFieldTypes,
    companyGeneralFieldTypes,
    companyOtherInfoFieldTypes,
} from './fieldTypes'

export const companyGeneralInputs = () => {
    const inputs = companyGeneralFieldTypes()
    return {
        sideForm: [
            inputs[companyGeneralFields.ID],
            inputs[companyGeneralFields.NAME],
            inputs[companyGeneralFields.FULL_NAME],
            inputs[companyGeneralFields.CONTACT_LEGAL_FORM_ID],
            inputs[companyGeneralFields.TAX_NUMBER],
            inputs[companyGeneralFields.INITIAL_CONTACT_DATE],
            inputs[companyGeneralFields.CONTACT_PRIORITY_ID],
            inputs[companyGeneralFields.DATA_SOURCE_ID],
            inputs[companyGeneralFields.CONTACT_STATUS_ID],
        ],
        mainForm: [
            {
                type: 'fieldArray',
                name: 'contact_phone_group',
                children: [inputs[companyGeneralFields.PHONE_NUMBER], inputs[companyGeneralFields.CONTACT_PHONE_TYPE_ID]],
            },
            {
                type: 'fieldArray',
                name: 'contact_email_group',
                children: [inputs[companyGeneralFields.EMAIL], inputs[companyGeneralFields.CONTACT_EMAIL_TYPE_ID]],
            },
            {
                type: 'array',
                name: 'websiteGroup',
                children: [inputs[companyGeneralFields.WEBSITE]],
            },
            {
                type: 'array',
                name: 'addressGroup',
                children: [inputs[companyGeneralFields.STREET], inputs[companyGeneralFields.STREET_NUMBER]],
            },
            {
                type: 'array',
                name: 'postalCodeGroup',
                children: [inputs[companyGeneralFields.ZIP_CODE], inputs[companyGeneralFields.CITY], inputs[companyGeneralFields.CONTACT_COUNTRY_ID]],
            },
            {
                type: 'array',
                name: 'queryCategoryroup',
                children: [inputs[companyGeneralFields.CONTACT_QUERY_CATEGORY_ID]],
            },
            {
                type: 'array',
                name: 'agentGroup',
                children: [inputs[companyGeneralFields.AGENT_ID]],
            },
        ],
    }
}

export const companyOtherInfoInputs = () => {
    const inputs = companyOtherInfoFieldTypes()

    return {
        mainForm: [
            {
                type: 'array',
                name: 'companyGroup',
                children: [
                    inputs[companyOtherInfoFields.CONTACT_CORE_BUSINESS_ID],
                    inputs[companyOtherInfoFields.CONTACT_COMPANY_SIZE_ID],
                    inputs[companyOtherInfoFields.EMPLOYEES_NO],
                    inputs[companyOtherInfoFields.BANK_ACCOUNT_NO],
                    inputs[companyOtherInfoFields.REGISTRY_COURT],
                    inputs[companyOtherInfoFields.SHARE_CAPITAL],
                ],
            },
            inputs[companyOtherInfoFields.NOTES],
        ],
    }
}

export const companyProfileInputs = (appSettings, propertyTypeValue, isEdit, featureFlags) => {
    const propertyType = Number(propertyTypeValue)
    const { house, flat, officeSpace, land, garage } = REAL_ESTATE_TYPES

    const isHouse = propertyType === house
    const isFlat = propertyType === flat
    const isOffice = propertyType === officeSpace
    const isLand = propertyType === land
    const isGarage = propertyType === garage
    const isRooms = [house, flat, officeSpace].includes(propertyType)
    const isBedrooms = [house, flat].includes(propertyType)

    const inputs = contactProfileFieldTypes(appSettings, isEdit, 'company')

    return {
        sideForm: [
            inputs[companyProfileFields.CONTACT_PROFILE_SERVICE_ID],
            inputs[companyProfileFields.PROPERTY_TYPE_ID],
            inputs[companyProfileFields.PROPERTY_SUB_TYPE_ID],
            inputs[companyProfileFields.CONTACT_PROFILE_STATUS_ID],
            inputs[companyProfileFields.CONTACT_FINANCING_ID],
            inputs[companyProfileFields.CONTACT_PROFILE_DURATION_ID],
            inputs[companyProfileFields.COMPANY_DEFINITION_ID],
            ...(featureFlags?.automatic_offer_sending ? [inputs[companyProfileFields.AUTOMATIC_OFFER_ENABLED]] : []),
        ],
        mainForm: [
            {
                type: 'array',
                name: 'locationGroup',
                children: [
                    inputs[companyProfileFields.COUNTRY_ID],
                    inputs[companyProfileFields.COUNTY_ID],
                    inputs[companyProfileFields.CITY_ID],
                    inputs[companyProfileFields.WIDER_AREA_ID],
                    inputs[companyProfileFields.QUARTER_ID],
                    inputs[companyProfileFields.ISLAND_ID],
                ],
            },
            {
                type: 'grouped',
                name: 'priceGroup',
                colProps: {
                    sm: 12,
                    xxl: 4,
                },
                items: [inputs[companyProfileFields.PRICE_FROM], inputs[companyProfileFields.PRICE_TO]],
            },
            {
                type: 'grouped',
                name: 'areaGroup',
                colProps: {
                    sm: 12,
                    xxl: 4,
                },
                items: [inputs[companyProfileFields.AREA_FROM], inputs[companyProfileFields.AREA_TO]],
            },
            !isLand && {
                type: 'grouped',
                name: 'constructionYearGroup',
                colProps: {
                    sm: 12,
                    xxl: 4,
                },
                items: [inputs[companyProfileFields.CONSTRUCTION_YEAR_FROM], inputs[companyProfileFields.CONSTRUCTION_YEAR_TO]],
            },
            isRooms && {
                type: 'grouped',
                name: 'roomsGroup',
                colProps: {
                    sm: 12,
                    xxl: 4,
                },
                items: [inputs[companyProfileFields.ROOMS_FROM], inputs[companyProfileFields.ROOMS_TO]],
            },
            isBedrooms && {
                type: 'grouped',
                name: 'bedRoomsGroup',
                colProps: {
                    sm: 12,
                    xxl: 4,
                },
                items: [inputs[companyProfileFields.BEDROOMS_FROM], inputs[companyProfileFields.BEDROOMS_TO]],
            },
            {
                type: 'array',
                name: 'floorGroup',
                children: [
                    ...(isHouse ? [inputs[companyProfileFields.PROPERTY_FLOOR_ID]] : []),
                    ...(isFlat || isOffice
                        ? [
                              inputs[companyProfileFields.PROPERTY_FLOOR_POSITION_ID],
                              inputs[companyProfileFields.FLOOR_NO_FROM],
                              inputs[companyProfileFields.FLOOR_NO_TO],
                          ]
                        : []),
                    ...(isFlat ? [inputs[companyProfileFields.EXCLUDE_BASEMENT], inputs[companyProfileFields.EXCLUDE_LAST_FLOOR]] : []),
                ],
            },
            {
                type: 'array',
                name: 'relatedStuffGroup',
                children: [
                    ...(isRooms ? [inputs[companyProfileFields.PROPERTY_SPACE_ID]] : []),
                    ...(!isGarage ? [inputs[companyProfileFields.PROPERTY_DESCRIPTIONS_ID]] : []),
                    ...(!isGarage && !isLand ? [inputs[companyProfileFields.PROPERTY_EQUIPMENT_ID]] : []),
                    ...(!isGarage ? [inputs[companyProfileFields.PROPERTY_UTILITY_ID]] : []),
                    ...(!isGarage && !isLand ? [inputs[companyProfileFields.PROPERTY_HEATING_ID]] : []),
                ],
            },
            inputs[companyProfileFields.SPECIAL_REQUEST],
        ],
    }
}

export const companyDetailSearchInputs = () => {
    const inputs = companyDetailSearchFieldTypes()

    return {
        config: {
            direction: FORM_DIRECTION.COLUMN,
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        columns: [
            [
                inputs[responseFields.NAME],
                inputs[responseFields.CONTACT_PHONE],
                inputs[responseFields.CONTACT_EMAIL],
                inputs[responseFields.AGENT],
                {
                    type: 'grouped',
                    name: 'initialContactGrouped',
                    label: i18n.t('app.common.contact.establishmentDate'),
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.INITIAL_CONTACT_DATE_FROM], inputs[responseFields.INITIAL_CONTACT_DATE_TO]],
                },
                {
                    type: 'grouped',
                    name: 'previousActivityGrouped',
                    label: i18n.t('app.common.company.previousActivity'),
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.LAST_ACTIVITY_DATE_FROM], inputs[responseFields.LAST_ACTIVITY_DATE_TO]],
                },
            ],
            [inputs[responseFields.CONTACT_COUNTRY], inputs[responseFields.CITY]],
            [
                inputs[responseFields.CONTACT_STATUS],
                inputs[responseFields.CONTACT_PROFILE_SERVICE],
                inputs[responseFields.CONTACT_PROFILE_STATUS],
                inputs[responseFields.CONTACT_QUERY_CATEGORY],
                inputs[responseFields.CONTACT_PRIORITY],
                inputs[responseFields.DATA_SOURCE],
            ],
        ],
    }
}

export const companyProfileDetailSearchInputs = (appSettings) => {
    const inputs = contactProfileDetailSearchFieldTypes()
    return {
        config: {
            direction: FORM_DIRECTION.COLUMN,
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        columns: [
            [
                inputs[responseFields.PROPERTY_TYPE],
                inputs[responseFields.PROPERTY_SUB_TYPE],
                {
                    type: 'grouped',
                    name: 'priceGrouped',
                    label: `${i18n.t('table.header.profile.price')} ${renderCurrency(appSettings?.currency)}`,
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.PRICE_FROM], inputs[responseFields.PRICE_TO]],
                },
                {
                    type: 'grouped',
                    name: 'roomsGrouped',
                    label: i18n.t('app.common.rooms'),
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.ROOMS_FROM], inputs[responseFields.ROOMS_TO]],
                },
                {
                    type: 'grouped',
                    name: 'bedroomsGrouped',
                    label: i18n.t('app.common.bedrooms'),
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.BEDROOMS_FROM], inputs[responseFields.BEDROOMS_TO]],
                },
            ],
            [
                inputs[responseFields.CONTACT_PROFILE_DURATION],
                {
                    type: 'empty-column',
                    name: 'emptyColumn1',
                    colProps: {
                        sm: 0,
                        xl: 12,
                    },
                },
                {
                    type: 'grouped',
                    name: 'areaGrouped',
                    label: i18n.t('app.common.spaceArea'),
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.AREA_FROM], inputs[responseFields.AREA_TO]],
                },
                {
                    type: 'grouped',
                    name: 'floorGrouped',
                    label: i18n.t('app.common.floorNumber'),
                    colProps: {
                        sm: 12,
                        // xl: 4
                    },
                    items: [inputs[responseFields.FLOOR_FROM], inputs[responseFields.FLOOR_TO]],
                },
                inputs[responseFields.PROPERTY_FLOOR],
            ],
            [
                inputs[responseFields.COUNTRY],
                inputs[responseFields.COUNTY],
                inputs[responseFields.CITY],
                inputs[responseFields.WIDER_AREA],
                inputs[responseFields.QUARTER],
            ],
        ],
    }
}
